import { useBrandQuery } from "entities/brands/queries/useBrandQuery";
import { useSettingsListByBrandQuery } from "entities/brands/settings/queries/useSettingsListByBrandQuery";
import { BrandSetting } from "entities/brands/settings/types";
import { useGamesData } from "entities/game/hooks/useGamesData";
import { useCreateGameNameAssistantMutation } from "entities/languagesAssistants/mutations/mutateCreateGameNameAssistant";
import { useCreateGeneralAssistantMutation } from "entities/languagesAssistants/mutations/mutateCreateGeneralAssistant";
import { useLanguageAssistantsQuery } from "entities/languagesAssistants/queries/useLanguageAssistantsQuery";
import { BrandTabs } from "entities/pages/types";
import { FC, PropsWithChildren, useState, useEffect } from "react";
import { generatePath, useNavigate, useParams } from "react-router-dom";
import { useDrawerState } from "shared/hooks/useDrawerState";
import { BRAND_SUB_PAGE_PATH } from "shared/routes/paths";
import { createContext } from "shared/utils/context";
import {
  BrandSettingsColumnsProps,
  ExtendedBrandLanguage,
  SettingsTabs,
} from "../types";
import { BrandSettingsPageType } from "./types";

export const SETTINGS_TAB_STORAGE_KEY = "brandSettingsTab";

const [Provider, useBrandSettingsPage] = createContext<BrandSettingsPageType>({
  name: "BrandSettingsPageContext",
  hookName: "useBrandSettingsPage",
  providerName: "BrandSettingsPageProvider",
});

const BrandSettingsPageProvider: FC<PropsWithChildren> = ({ children }) => {
  const { id } = useParams();
  const pageDrawer = useDrawerState<BrandSetting>();
  const languageDetailsDrawer = useDrawerState<ExtendedBrandLanguage>();
  const brandId = String(id);
  const [settingsTab, setSettingsTab] = useState<SettingsTabs>(
    SettingsTabs.LANGUAGES,
  );

  const [isLoadingAssistants, setIsLoadingAssistants] = useState(false);

  const navigate = useNavigate();

  const {
    searchData: searchGamesData,
    isLoading: isLoadingGames,
    tableData: gamesList,
  } = useGamesData({
    isActive: [],
    size: 10,
  });

  const { data: brandDetails, isPending: isPendingBrandDetails } =
    useBrandQuery(brandId);
  const { data: settingsList = [], isLoading } =
    useSettingsListByBrandQuery(brandId);

  const { mutateAsync: createGameNameAssistant } =
    useCreateGameNameAssistantMutation();

  const { mutateAsync: createGeneralAssistant } =
    useCreateGeneralAssistantMutation();

  useLanguageAssistantsQuery();

  useEffect(() => {
    const cachedSettingsTab = localStorage.getItem(SETTINGS_TAB_STORAGE_KEY);

    if (cachedSettingsTab === SettingsTabs.SEARCH) {
      setSettingsTab(SettingsTabs.SEARCH);
      localStorage.removeItem(SETTINGS_TAB_STORAGE_KEY);
    }
  }, []);

  const handleCreateDefaultAssistants = async () => {
    try {
      setIsLoadingAssistants(true);
      const [generalAssistant, gameNameAssistant] = await Promise.all([
        createGeneralAssistant(),
        createGameNameAssistant(),
      ]);

      return {
        generalAssistant,
        gameNameAssistant,
      };
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoadingAssistants(false);
    }
  };

  const columnsParams: BrandSettingsColumnsProps = {
    handleOpenSettingDetailPage: (tabDetailsId: string) =>
      navigate(
        generatePath(BRAND_SUB_PAGE_PATH, {
          id: brandId,
          tabId: BrandTabs.Settings,
          tabDetailsId: String(tabDetailsId),
        }),
      ),
  };

  const handleAddNewLanguage = async () => {
    const assistants = await handleCreateDefaultAssistants();

    if (assistants?.generalAssistant && assistants?.gameNameAssistant) {
      languageDetailsDrawer.openDrawer({
        id: "",
        displayName: "",
        code: "",
        createdAt: new Date(),
        updatedAt: new Date(),
        generalAssistantId: assistants.generalAssistant.assistantId,
        gameNameAssistantId: assistants.gameNameAssistant.assistantId,
      });
    }
  };

  const isLoadingPages = isLoading || isPendingBrandDetails || isLoadingGames;

  const value = {
    data: settingsList,
    pageDrawer,
    gamesList,
    searchGamesData,
    columnsParams,
    isLoadingPages,
    brandDetails,
    settingsTab,
    setSettingsTab,
    handleCreateDefaultAssistants,
    isLoadingAssistants,
    handleAddNewLanguage,
    languageDetailsDrawer,
  };

  return <Provider value={value}>{children}</Provider>;
};

export { BrandSettingsPageProvider, useBrandSettingsPage };
